import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ShieldCheck, Home, FileText, FileCheck, Settings, LogOut, Menu, X, FileSpreadsheet, BarChart3, Users, AlertCircle, CreditCard, Receipt } from "lucide-react";
import AxiosDB from "@/Config/AxiosDB";
import { toast } from "sonner";
import ServerStatus from "./ServerStatus";

export default function Layout({ children, user, onLogout, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [superadminName, setSuperadminName] = useState("");

  useEffect(() => {
    const impersonating = localStorage.getItem("impersonating") === "true";
    const adminName = localStorage.getItem("superadmin_name") || "";
    setIsImpersonating(impersonating);
    setSuperadminName(adminName);
  }, []);

  const handleReturnToSuperAdmin = () => {

    AxiosDB.get('/admin/impersonate/panel')
      .then(({ data }) => {
        localStorage.setItem("token", data.access_token);
        console.log(data)
        toast.success(`Accediendo como Administrador`);

        localStorage.removeItem("impersonating");
        localStorage.removeItem("superadmin_name");
        navigate("/admin/empresas");
        window.location.reload();
      })
      .catch((error) => {
        toast.error(error.response?.data?.detail || "Error al acceder a la empresa");
      })

  };

  const menuItems = [
    { icon: Home, label: "Dashboard", path: "/", testId: "nav-dashboard" },
    { icon: FileText, label: "Comprobantes Emitidos", path: "/emitidos", testId: "nav-emitidos" },
    { icon: FileCheck, label: "Comprobantes Recibidos", path: "/recibidos", testId: "nav-recibidos" },
    { icon: FileSpreadsheet, label: "Configuración NCF", path: "/configuracion-ncf", testId: "nav-configuracion-ncf" },
    { icon: BarChart3, label: "Reportes", path: "/reportes", testId: "nav-reportes" },
    { icon: Receipt, label: "Reporte de Impuesto (IT-1)", path: "/reporte-impuesto", testId: "nav-reporte-impuesto" },

    { icon: CreditCard, label: "Facturación", path: "/facturacion", testId: "nav-facturacion" },

    ...(user?.role === "superadmin" ? [
      { icon: Users, label: "Administrar Empresas", path: "/admin/empresas", testId: "nav-admin-empresas", badge: "Admin" }
    ] : []),
    { icon: Settings, label: "Configuración de Usuario", path: "/configuracion", testId: "nav-configuracion" },
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 mt-50" style={{ marginTop: isImpersonating ? 53 : 0 }} >
      {/* Banner de Impersonating */}
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-600 to-purple-700 text-white py-2 px-4 print:hidden">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm font-medium">
                Estás viendo como: <strong>{user?.nombre}</strong> ({user?.rnc})
              </span>
              {superadminName && (
                <span className="text-xs opacity-90">
                  - SuperAdmin: {superadminName}
                </span>
              )}
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleReturnToSuperAdmin}
              className="bg-white text-purple-700 hover:bg-purple-50"
            >
              Volver a Panel Admin
            </Button>
          </div>
        </div>
      )}
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-8 h-8 text-blue-600" />
          <span className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Buzon Fiscal</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 bg-white border-r border-slate-200 z-40 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-slate-200" style={{ paddingTop: isImpersonating ? 71 : 30 }}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Buzon Fiscal
              </h1>
              <p className="text-xs text-slate-500">Sistema de Comprobantes</p>
            </div>
          </div>
          <div className="mt-4">
            <ServerStatus />
          </div>
        </div>

        <nav className="p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${active
                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                  : "text-slate-600 hover:bg-slate-50"
                  }`}
                data-testid={item.testId}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid="user-menu-trigger">
                <Avatar className="w-10 h-10">
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-700 text-white font-semibold">
                    {getInitials(user?.nombre_usuario)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-slate-800">{user?.nombre_usuario}</p>
                  <p className="t9userext-xs text-slate-500">{user?.correo}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleNavigation("/configuracion")} data-testid="menu-settings">
                <Settings className="mr-2 h-4 w-4" />
                <span>Configuración</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} className="text-red-600" data-testid="menu-logout">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Cerrar Sesión</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen pt-20 lg:pt-0">
        <div className="p-6 lg:p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="page-title">
              {title}
            </h1>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}